import type { ApiV1Paths, ApiV1Schemas } from "@lureo/editor-contracts";
import createClient from "openapi-fetch";

// The release console's "is live" report, as lureo-frontend sends it (src/releaseSighting.ts and main.tsx).

type Sighting = ApiV1Schemas["ReportClientSightingRequest"];
type Receipt = ApiV1Schemas["ClientSightingReceiptDto"];
type Timer = ReturnType<typeof setTimeout>;

// The release lane's product; the console matches a sighting to its lane by this name.
export const SIGHTING_PLATFORM = "three";

// The contract's ApiKey security scheme (components.securitySchemes.ApiKey.name), which openapi-typescript does not
// emit; ApiV1Client sends the same header.
export const API_KEY_HEADER = "X-Api-Key";

export interface SightingOptions {
  // reportClientSighting; null when it was not answered with a receipt.
  readonly report: (sighting: Sighting, signal: AbortSignal) => Promise<Receipt | null>;
  // getVersion's environment, or null when unread.
  readonly environment: (signal: AbortSignal) => Promise<string | null>;
  readonly schedule?: (callback: () => void, delay: number) => Timer;
  readonly cancel?: (timer: Timer) => void;
}

// Retries end only once this exact build is durably acknowledged. Outside production nothing is recorded, so a
// staging deployment ends them once it is known to be staging.
export function startReleaseSighting(sighting: Sighting, options: SightingOptions): () => void {
  const { report: send, environment } = options;
  const schedule = options.schedule ?? ((callback, delay) => setTimeout(callback, delay));
  const cancel =
    options.cancel ??
    ((timer) => {
      clearTimeout(timer);
    });
  const expected = {
    platform: sighting.platform ?? "",
    version: sighting.version ?? "",
    sha: (sighting.sha ?? "").toLowerCase(),
    unitySha: (sighting.unitySha ?? "").toLowerCase(),
  };
  let stopped = false;
  let delay = 2000;
  let retry: Timer | undefined;
  let timeout: Timer | undefined;
  let controller: AbortController | undefined;

  const report = async (): Promise<void> => {
    if (stopped) return;
    const current = new AbortController();
    controller = current;
    const deadline = schedule(() => {
      current.abort();
    }, 10000);
    timeout = deadline;
    try {
      const receipt = await send(sighting, current.signal);
      const identity = receipt?.identity;
      if (
        receipt?.recorded === true &&
        /^[a-f0-9]{40}$/.test(expected.sha) &&
        identity?.platform === expected.platform &&
        identity.version === expected.version &&
        identity.sha === expected.sha &&
        identity.unitySha === expected.unitySha
      ) {
        stopped = true;
      } else if (receipt !== null && (await environment(current.signal))?.toLowerCase() === "staging") {
        stopped = true;
      }
    } catch {
      // Transient failures retry; a receipt is never assumed.
    } finally {
      cancel(deadline);
      timeout = undefined;
    }
    if (!stopped) {
      retry = schedule(() => void report(), delay);
      delay = Math.min(delay * 2, 60000);
    }
  };

  void report();
  return () => {
    stopped = true;
    if (retry !== undefined) cancel(retry);
    if (timeout !== undefined) cancel(timeout);
    controller?.abort();
  };
}

export interface SightingClientOptions {
  readonly backendUrl: string;
  readonly apiKey: string;
  readonly fetch: typeof globalThis.fetch;
}

// The two typed /api/v1 operations the sighting needs, on the app key alone.
export function sightingClient(options: SightingClientOptions): Pick<SightingOptions, "report" | "environment"> {
  const client = createClient<ApiV1Paths>({ baseUrl: options.backendUrl, fetch: options.fetch });
  const headers = { [API_KEY_HEADER]: options.apiKey };
  return {
    report: async (body, signal) => {
      const { data } = await client.POST("/api/v1/client-sightings", {
        body,
        headers,
        signal,
        cache: "no-store",
        credentials: "omit",
      });
      return data ?? null;
    },
    environment: async (signal) => {
      const { data } = await client.GET("/api/v1/version", { headers, signal, cache: "no-store", credentials: "omit" });
      return data?.environment ?? null;
    },
  };
}
